import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { getUserInfo, type UserInfo, type SysMenuDataItem } from "@/services/login";

interface UserState {
  userInfo: UserInfo | null;
  loading: boolean;
  setUserInfo: (userInfo: UserInfo | null) => void;
  fetchUserInfo: () => Promise<UserInfo | null>;
  addDynamicMenus: (menus: SysMenuDataItem[]) => void;
  logout: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      userInfo: null,
      loading: false,

      setUserInfo: (userInfo) => set({ userInfo }),

      fetchUserInfo: async () => {
        set({ loading: true });
        try {
          const res = await getUserInfo();
          set({ userInfo: res, loading: false });
          return res;
        } catch (error) {
          set({ userInfo: null, loading: false });
          return null;
        }
      },

      addDynamicMenus: (menus) => {
        const { userInfo } = get();
        if (!userInfo) {
          return;
        }

        const existingAddresses = new Set<string>();
        const collectAddresses = (items: SysMenuDataItem[]) => {
          for (const item of items) {
            if (item.address) {
              existingAddresses.add(item.address);
            }
            if (item.children) {
              collectAddresses(item.children);
            }
          }
        };
        collectAddresses(userInfo.menus);

        const newMenus = menus.filter(
          (m) => !existingAddresses.has(m.address),
        );
        if (newMenus.length === 0) {
          return;
        }

        set({
          userInfo: {
            ...userInfo,
            menus: [...userInfo.menus, ...newMenus],
          },
        });
      },

      logout: () => {
        set({ userInfo: null });
        localStorage.removeItem("token");
      },
    }),
    {
      name: "user-storage", // 存储在 localStorage 中的 key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ userInfo: state.userInfo }), // 只持久化 userInfo
    },
  ),
);
